import React, { useState, useEffect } from "react";
import { MdAdd, MdEdit, MdArrowBack, MdClose, MdDelete } from "react-icons/md";
import {
  createProject,
  getallCustomerDropdown,
  getallEmployeesDropdown,
  getAllMetrials,
  getallProcess,
  getallVendorDropdown,
  UpdateprojectbyId,
} from "../../../api/api";
import toast from "react-hot-toast";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ number, title, subtitle }) => (
  <div className="flex items-center gap-4 mb-6">
    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-black flex items-center justify-center">
      <span className="text-white text-sm font-bold">{number}</span>
    </div>
    <div>
      <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest">{title}</h2>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex-1 h-px bg-gray-100 ml-2" />
  </div>
);

// ─── Field Wrapper ────────────────────────────────────────────────────────────
const Field = ({ label, required, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
      {label}
      {required && <span className="text-gray-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

// ─── Input ────────────────────────────────────────────────────────────────────
const inputCls = (disabled) =>
  `w-full px-3 py-2.5 text-sm rounded-lg border transition-all outline-none ${
    disabled
      ? "bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed"
      : "bg-white border-gray-200 text-gray-900 hover:border-gray-300 focus:border-gray-500 focus:ring-2 focus:ring-gray-50"
  }`;

// ─── Main Component ───────────────────────────────────────────────────────────
const AddProject = ({ onBack, isEdit, isView, currentProject }) => {
  const [showProcessCard, setShowProcessCard] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [processes, setProcess] = useState([]);
  const [metrials, setMetrials] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allData, setData] = useState(currentProject?.processes || []);
  const [selectedData, setSelectedData] = useState({ employee: "", process: "", vendor: "", isAssigned: true });
  const [isEditing, setIsEditing] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [dispatches, setDispatches] = useState(currentProject?.dispatches || []);
  const [openDispatch, setOpenDispatch] = useState(false);

  const [projectData, setProjectData] = useState(
    currentProject || {
      name: "", customerID: "", workOrder: "", partNo: "",
      quantity: "", description: "", projectValue: "",
      metrialName: "", type: "",
      dimensions: { dia: "", thickness: "", breath: "", length: "" },
      startDate: "", completionDate: "", revision: "", drawingFile: null,
    }
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [c, p, e, v, m] = await Promise.all([
          getallCustomerDropdown(), getallProcess(),
          getallEmployeesDropdown(), getallVendorDropdown(), getAllMetrials(),
        ]);
        setCustomers(c.data); setProcess(p.data);
        setEmployees(e.data); setVendors(v.data); setMetrials(m.data);
      } catch (err) {
        toast.error("Failed to fetch data.");
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProjectData((p) => ({ ...p, [name]: value }));
  };

  const handleDimensionsChange = (e) => {
    const { name, value } = e.target;
    setProjectData((p) => ({ ...p, dimensions: { ...p.dimensions, [name]: value } }));
  };

  const handleFileUpload = (e) => {
    setProjectData((p) => ({ ...p, drawingFile: e.target.files[0] }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectedData((p) => ({ ...p, [name]: value }));
  };

  const handleAssign = () => {
    const { employee, process, vendor } = selectedData;
    if (!employee || !process || !vendor) { toast.error("Please complete all selections."); return; }
    if (isEditing) {
      const updated = [...allData];
      updated[editIndex] = selectedData;
      setData(updated);
      setIsEditing(false); setEditIndex(null);
      toast.success("Process updated.");
    } else {
      setData((prev) => [...prev, selectedData]);
      toast.success("Process assigned.");
    }
    setSelectedData({ employee: "", process: "", vendor: "" });
  };

  const handleEdit = (index) => {
    setSelectedData(allData[index]);
    setIsEditing(true); setEditIndex(index);
  };

  const handleDelete = (item) => {
    setData((prev) => prev.filter((d) => d !== item));
    toast.success("Deleted.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentProject && allData.length === 0) {
      toast.error("Please assign at least one process."); return;
    }
    setIsSubmitting(true);
    try {
      const payload = { ...projectData, processes: allData, dispatches };
      if (currentProject) {
        await UpdateprojectbyId(currentProject._id, payload);
      } else {
        await createProject(payload);
      }
      toast.success("Saved successfully!");
      onBack();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCircle = projectData.type === "circle";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className=" mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            >
              <MdArrowBack className="text-gray-600 text-lg" />
            </button>
            <div>
              <h1 className="text-base font-bold text-gray-900">
                {isView ? "View Project" : isEdit ? "Edit Project" : "New Project"}
              </h1>
              <p className="text-xs text-gray-400">
                {isView ? "Read-only mode" : isEdit ? "Update project details" : "Fill in the project information below"}
              </p>
            </div>
          </div>

          {!isView && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowProcessCard(!showProcessCard)}
                className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {showProcessCard ? "Hide" : "Show"} Processes
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-5 py-2 text-sm font-bold text-white rounded-lg transition-all ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-black hover:bg-gray-700 active:scale-95 shadow-sm"
                }`}
              >
                {isSubmitting ? "Saving…" : isEdit ? "Update Project" : "Save Project"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">

        {/* ── Section 1: Basic Info ─────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <SectionHeader number="01" title="Basic Information" subtitle="Project identity and client details" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field label="Project Name" required>
              <input
                type="text" name="name" disabled={isView}
                value={projectData.name} onChange={handleInputChange}
                placeholder="e.g. Turbine Shaft Batch A"
                className={inputCls(isView)} required
              />
            </Field>

            <Field label="Company Name" required>
              <select
                name="customerID" disabled={isView}
                value={currentProject ? currentProject.customerID?._id : projectData.customerID}
                onChange={handleInputChange}
                className={inputCls(isView)} required
              >
                <option value="">Select Customer</option>
                {customers.map((c) => (
                  <option key={c._id} value={c._id}>{c.companyName}</option>
                ))}
              </select>
            </Field>

            <Field label="Purchase No" required>
              <input
                name="workOrder" disabled={isView}
                value={projectData.workOrder} onChange={handleInputChange}
                placeholder="e.g. PO-2024-001"
                className={inputCls(isView)} required
              />
            </Field>

            <Field label="Part Number" required>
              <input
                name="partNo" disabled={isView}
                value={projectData.partNo} onChange={handleInputChange}
                placeholder="e.g. PT-10023"
                className={inputCls(isView)} required
              />
            </Field>

            <Field label="Revision">
              <input
                type="text" name="revision" disabled={isView}
                value={projectData.revision} onChange={handleInputChange}
                placeholder="e.g. Rev A"
                className={inputCls(isView)}
              />
            </Field>

            <Field label="Quantity" required>
              <input
                type="number" name="quantity" disabled={isView}
                value={projectData.quantity} onChange={handleInputChange}
                placeholder="0"
                className={inputCls(isView)} required
              />
            </Field>
          </div>
        </div>

        {/* ── Section 2: Material & Dimensions ─────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <SectionHeader number="02" title="Material & Dimensions" subtitle="Raw material type and physical specifications" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field label="Raw Material" required>
              <select
                name="metrialName" disabled={isView}
                value={projectData.metrialName} onChange={handleInputChange}
                className={inputCls(isView)} required
              >
                <option value="" disabled>Select Material</option>
                {metrials.map((m) => (
                  <option key={m.name} value={m.name}>{m.name}</option>
                ))}
              </select>
            </Field>

            <Field label="Type" required>
              <select
                name="type" disabled={isView}
                value={projectData.type} onChange={handleInputChange}
                className={inputCls(isView)} required
              >
                <option value="" disabled>Select Type</option>
                <option value="circle">Circle</option>
                <option value="rectangle">Rectangle</option>
              </select>
            </Field>

            {/* Dynamic dimension fields */}
            {projectData.type === "circle" ? (
              <>
                <Field label="Diameter (mm)" required>
                  <input
                    type="number" name="dia" disabled={isView}
                    value={projectData.dimensions?.dia} onChange={handleDimensionsChange}
                    placeholder="0.00" className={inputCls(isView)} required
                  />
                </Field>
                <Field label="Length (mm)" required>
                  <input
                    type="number" name="length" disabled={isView}
                    value={projectData.dimensions?.length} onChange={handleDimensionsChange}
                    placeholder="0.00" className={inputCls(isView)} required
                  />
                </Field>
              </>
            ) : projectData.type === "rectangle" ? (
              <>
                <Field label="Thickness (mm)" required>
                  <input
                    type="number" name="thickness" disabled={isView}
                    value={projectData.dimensions?.thickness} onChange={handleDimensionsChange}
                    placeholder="0.00" className={inputCls(isView)} required
                  />
                </Field>
                <Field label="Breadth (mm)" required>
                  <input
                    type="number" name="breath" disabled={isView}
                    value={projectData.dimensions?.breath} onChange={handleDimensionsChange}
                    placeholder="0.00" className={inputCls(isView)} required
                  />
                </Field>
                <Field label="Length (mm)" required>
                  <input
                    type="number" name="length" disabled={isView}
                    value={projectData.dimensions?.length} onChange={handleDimensionsChange}
                    placeholder="0.00" className={inputCls(isView)} required
                  />
                </Field>
              </>
            ) : (
              <div className="col-span-1 md:col-span-2 flex items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 h-12">
                <span className="text-xs text-gray-400">Select a type to configure dimensions</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Section 3: Project Value & Dates ──────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <SectionHeader number="03" title="Value & Timeline" subtitle="Financial details and project schedule" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field label="Project Value" required>
              <div className="flex gap-2">
                <select
                  name="currency" disabled={isView}
                  value={projectData.currency} onChange={handleInputChange}
                  className={`${inputCls(isView)} w-20 flex-shrink-0`}
                >
                  <option value="INR">₹ INR</option>
                  <option value="USD">$ USD</option>
                  <option value="EUR">€ EUR</option>
                  <option value="JPY">¥ JPY</option>
                  <option value="GBP">£ GBP</option>
                </select>
                <input
                  type="number" name="projectValue" disabled={isView}
                  value={projectData.projectValue} onChange={handleInputChange}
                  placeholder="0.00" className={`${inputCls(isView)} flex-1`} required
                />
              </div>
            </Field>

            <Field label="Start Date" required>
              <input
                type="date" name="startDate" disabled={isView}
                value={projectData.startDate?.slice(0, 10)} onChange={handleInputChange}
                className={inputCls(isView)} required
              />
            </Field>

            <Field label="Completion Date" required>
              <input
                type="date" name="completionDate" disabled={isView}
                value={projectData.completionDate?.slice(0, 10)} onChange={handleInputChange}
                className={inputCls(isView)} required
              />
            </Field>

            <Field label="Description" required className="md:col-span-2">
              <textarea
                name="description" disabled={isView}
                value={projectData.description} onChange={handleInputChange}
                placeholder="Describe the project scope, requirements, or special notes…"
                rows={3}
                className={`${inputCls(isView)} resize-none`} required
              />
            </Field>

            <Field label="Drawing / PDF">
              <div className="relative">
                <input
                  type="file" disabled={isView}
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-500 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-gray-50 file:text-gray-600 hover:file:bg-gray-100 cursor-pointer disabled:opacity-50"
                />
              </div>
              {currentProject?.drawingFile && (
                <a
                  href={currentProject.drawingFile}
                  target="_blank" rel="noopener noreferrer"
                  className="text-xs text-gray-600 hover:text-gray-700 font-medium mt-1 flex items-center gap-1"
                >
                  <span>↗</span> View existing file
                </a>
              )}
            </Field>
          </div>

          {/* Dispatch trigger */}
          <div className="mt-5 pt-5 border-t border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-700">Dispatch Records</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {dispatches.length === 0 ? "No dispatches added yet" : `${dispatches.length} dispatch${dispatches.length > 1 ? "es" : ""} recorded`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpenDispatch(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 hover:border-gray-300 hover:text-gray-600 hover:bg-gray-50 transition-all text-gray-600"
            >
              <MdAdd size={16} />
              {isView ? "View" : isEdit ? "Manage" : "Add"} Dispatches
            </button>
          </div>
        </div>

        {/* ── Section 4: Process Assignment ─────────────────────────────────── */}
        {(showProcessCard || isView) && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <SectionHeader number="04" title="Process Assignment" subtitle="Assign processes to employees and vendors" />

            {/* Assigned processes list */}
            {allData.length > 0 ? (
              <div className="space-y-3 mb-6">
                {allData.map((data, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50 hover:border-gray-100 hover:bg-gray-50/30 transition-all"
                  >
                    <div className="flex items-center gap-6">
                      <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex gap-6">
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-1">Employee</p>
                          <span className="text-sm font-semibold text-gray-800">
                            {employees.find((emp) => emp._id === data.employee)?.name || "—"}
                          </span>
                        </div>
                        <div className="w-px bg-gray-200" />
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-1">Process</p>
                          <span className="text-sm font-semibold text-gray-800">
                            {processes.find((p) => p._id === data.process)?.name || "—"}
                          </span>
                        </div>
                        <div className="w-px bg-gray-200" />
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-1">Vendor</p>
                          <span className="text-sm font-semibold text-gray-800">
                            {vendors.find((v) => v._id === data.vendor)?.companyName || "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                    {!isView && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(index)}
                          className="p-2 rounded-lg hover:bg-white hover:shadow-sm text-gray-400 hover:text-gray-700 transition-all"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(data)}
                          className="p-2 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-500 transition-all"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 rounded-xl border border-dashed border-gray-200 bg-gray-50 mb-6">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <MdAdd className="text-gray-400 text-xl" />
                </div>
                <p className="text-sm font-medium text-gray-500">No processes assigned yet</p>
                <p className="text-xs text-gray-400 mt-1">Use the form below to add processes</p>
              </div>
            )}

            {/* Add / Edit process form */}
            {!isView && (
              <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
                  {isEditing ? "✎ Edit Process" : "+ Add New Process"}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <select
                    name="process" value={selectedData.process} onChange={handleChange}
                    className={inputCls(false)}
                  >
                    <option value="" disabled>Select Process</option>
                    {processes
                      .filter((proc) => !allData.some((p) => p.process === proc._id) || proc._id === selectedData.process)
                      .map((proc) => (
                        <option key={proc._id} value={proc._id}>{proc.name}</option>
                      ))}
                  </select>

                  <select
                    name="employee" value={selectedData.employee} onChange={handleChange}
                    className={inputCls(false)}
                  >
                    <option value="" disabled>Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp._id} value={emp._id}>{emp.name}</option>
                    ))}
                  </select>

                  <select
                    name="vendor" value={selectedData.vendor} onChange={handleChange}
                    className={inputCls(false)}
                  >
                    <option value="" disabled>Select Vendor</option>
                    {vendors.map((v) => (
                      <option key={v._id} value={v._id}>{v.companyName}</option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={handleAssign}
                    className="px-4 py-2.5 text-sm font-bold text-white bg-black hover:bg-gray-700 active:scale-95 rounded-lg transition-all shadow-sm"
                  >
                    {isEditing ? "Update" : "Assign"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bottom submit bar for view mode */}
        {isView && (
          <div className="flex justify-end">
            <button
              onClick={onBack}
              className="px-5 py-2.5 text-sm font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              ← Back
            </button>
          </div>
        )}
      </div>

      {/* ── Dispatch Drawer ────────────────────────────────────────────────── */}
      {openDispatch && (
        <AddDispatch
          quantity={projectData?.quantity}
          dispatchs={dispatches}
          setDispatchs={setDispatches}
          setOpenDispacth={setOpenDispatch}
          isView={isView}
          isEdit={isEdit}
        />
      )}
    </div>
  );
};

export default AddProject;

// ─── Input Field ──────────────────────────────────────────────────────────────
const InputField = ({ label, type, value, onChange, placeholder }) => (
  <div className="flex flex-col gap-1.5 mb-3">
    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</label>
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-900 outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-50 transition-all"
    />
  </div>
);

// ─── Dispatch Panel ───────────────────────────────────────────────────────────
const AddDispatch = ({ quantity, dispatchs, setDispatchs, setOpenDispacth, isView, isEdit }) => {
  const [dispatchQuantity, setDispatchQuantity] = useState("");
  const [dispatchDate, setDispatchDate] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);

  const remaining = quantity - dispatchs.reduce((sum, d) => sum + Number(d.quantity), 0);
  const usedPercent = quantity ? Math.min(100, ((quantity - remaining) / quantity) * 100) : 0;

  const handleAdd = () => {
    if (!dispatchQuantity.trim() || !dispatchDate.trim()) {
      alert("Please fill all fields"); return;
    }
    const qty = Number(dispatchQuantity);
    const limit = editingIndex !== null
      ? remaining + Number(dispatchs[editingIndex].quantity)
      : remaining;

    if (qty > limit) { alert(`Exceeds remaining quantity (${limit}).`); return; }

    if (editingIndex !== null) {
      const updated = [...dispatchs];
      updated[editingIndex] = { ...updated[editingIndex], quantity: qty, dispatchDate };
      setDispatchs(updated);
      setEditingIndex(null);
    } else {
      setDispatchs([...dispatchs, { quantity: qty, dispatchDate }]);
    }
    setDispatchQuantity(""); setDispatchDate("");
  };

  const handleEdit = (i) => {
    setEditingIndex(i);
    setDispatchQuantity(dispatchs[i].quantity);
    setDispatchDate(dispatchs[i].dispatchDate?.slice(0, 10));
  };

  const handleDelete = (i) => {
    if (window.confirm("Delete this dispatch?")) {
      setDispatchs(dispatchs.filter((_, idx) => idx !== i));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white w-full sm:w-96 h-full flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-bold text-gray-900">
              {isView ? "View" : isEdit ? "Edit" : "Manage"} Dispatches
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {isView ? "Read-only dispatch log" : "Track partial shipments"}
            </p>
          </div>
          <button
            onClick={() => setOpenDispacth(false)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <MdClose className="text-gray-500 text-lg" />
          </button>
        </div>

        {/* Quantity tracker */}
        {quantity && (
          <div className="px-5 py-4 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Dispatch Progress</span>
              <span className="text-xs font-bold text-gray-700">{quantity - remaining} / {quantity} units</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gray-500 rounded-full transition-all duration-500"
                style={{ width: `${usedPercent}%` }}
              />
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-xs text-gray-400">Dispatched</span>
              <span className="text-xs font-semibold text-gray-600">{remaining} remaining</span>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {/* Add/Edit form */}
          {!isView && (
            <div className="px-5 py-5 border-b border-gray-100">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
                {editingIndex !== null ? "✎ Edit Dispatch" : "+ New Dispatch"}
              </p>
              <InputField
                label="Quantity" type="number"
                value={dispatchQuantity}
                onChange={(e) => setDispatchQuantity(e.target.value)}
                placeholder="Enter quantity"
              />
              <InputField
                label="Dispatch Date" type="date"
                value={dispatchDate}
                onChange={(e) => setDispatchDate(e.target.value)}
                placeholder=""
              />
              <button
                onClick={handleAdd}
                className="w-full py-2.5 text-sm font-bold text-white bg-black hover:bg-gray-700 active:scale-95 rounded-lg transition-all mt-1 shadow-sm"
              >
                {editingIndex !== null ? "Update Dispatch" : "Save Dispatch"}
              </button>
              {editingIndex !== null && (
                <button
                  onClick={() => { setEditingIndex(null); setDispatchQuantity(""); setDispatchDate(""); }}
                  className="w-full py-2 text-xs text-gray-500 hover:text-gray-700 mt-2"
                >
                  Cancel edit
                </button>
              )}
            </div>
          )}

          {/* Dispatch list */}
          <div className="px-5 py-5">
            {dispatchs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <MdAdd className="text-gray-400 text-xl" />
                </div>
                <p className="text-sm text-gray-400">No dispatches yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">
                  History ({dispatchs.length})
                </p>
                {[...dispatchs].reverse().map((dispatch, i) => {
                  const realIndex = dispatchs.length - 1 - i;
                  return (
                    <div
                      key={i}
                      className="p-4 rounded-xl border border-gray-100 bg-gray-50 hover:border-gray-100 transition-all"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                            <span className="text-xs font-bold text-gray-600">{dispatchs.length - i}</span>
                          </div>
                          <span className="text-xs font-semibold text-gray-600">Dispatch #{dispatchs.length - i}</span>
                        </div>
                        {!isView && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEdit(realIndex)}
                              className="p-1.5 rounded-lg hover:bg-white hover:shadow-sm text-gray-400 hover:text-gray-700 transition-all"
                            >
                              <MdEdit size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(realIndex)}
                              className="p-1.5 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-500 transition-all"
                            >
                              <MdDelete size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Quantity</p>
                          <p className="text-sm font-bold text-gray-800">{dispatch.quantity} units</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Date</p>
                          <p className="text-sm font-bold text-gray-800">{dispatch.dispatchDate?.slice(0, 10)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};